import { Form } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useLazyGetBranchQuery } from '@app/api/Branches';
import { timezonesApi } from '@app/api/Timezones';
import type { ModelTimezone } from '@app/api/Timezones';
import { useSchedule } from '@app/components/Schedule';
import type { TagOptionType } from '@app/components/TagsSelect/types';
import { SENDOUTS_PATH } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  useEditSendoutMutation,
  useGetSendoutForEditQuery,
} from '@app/services/rtkQuery/Sendouts/api';
import { useErrorHandler } from '@app/shared/hooks';
import { useAppDispatch } from '@app/store';
import { fileToBase64, getContrastYIQ } from '@app/utils';

import type { EditSendoutsForm } from '@pages/Sendouts/EditSendouts/types';

const { useForm, useWatch } = Form;

export const useEditSendouts = () => {
  const { id } = useParams<{ id: string }>();
  const { showNotification } = useNotification();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [form] = useForm<EditSendoutsForm>();
  const formValues = useWatch([], form);
  const { getScheduleDate } = useSchedule();
  const [timezones, setTimezones] = useState<ModelTimezone[]>([]);
  const sendoutId = useMemo(() => {
    const numberId = Number(id);

    return isNaN(numberId) ? 0 : numberId;
  }, [id]);

  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const {
    data: sendoutData,
    isFetching: isSendoutFetching,
    isLoading: isSendoutLoading,
    error: sendoutError,
    isError: isSendoutError,
  } = useGetSendoutForEditQuery(
    { sendoutId, ActiveBranchId },
    {
      skip: !sendoutId,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );
  const [getBranch] = useLazyGetBranchQuery();
  const [editSendout] = useEditSendoutMutation();

  useErrorHandler([{ flag: isSendoutError, error: sendoutError }]);
  const onCancel = useCallback(() => {
    navigate(SENDOUTS_PATH);
  }, [navigate]);

  const getTimezonesHandler = useCallback(async () => {
    const request = dispatch(
      timezonesApi.endpoints.GetTimezones.initiate(undefined, {
        forceRefetch: true,
      })
    );

    try {
      return await request.unwrap();
    } catch (error) {
      showNotification(error);
      return Promise.reject(error);
    } finally {
      request.unsubscribe?.();
    }
  }, [dispatch, showNotification]);

  const getBranchHandler = useCallback(async () => {
    try {
      const response = await getBranch({
        ActiveBranchId,
        branchId: ActiveBranchId,
      }).unwrap();

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }, [getBranch, ActiveBranchId]);

  const initTimezones = useCallback(async () => {
    try {
      const [timezonesResponse, branchResponse] = await Promise.allSettled([
        getTimezonesHandler(),
        getBranchHandler(),
      ]);
      let timezones: ModelTimezone[] = [];
      let timezone: string = '';

      if (timezonesResponse.status === 'fulfilled') {
        timezones = timezonesResponse.value;
        setTimezones(timezones);
      }

      if (
        branchResponse &&
        branchResponse.status === 'fulfilled' &&
        branchResponse.value
      ) {
        const branch = branchResponse.value;
        timezone = branch.Timezone ?? '';
      }

      if (timezone) {
        const formattedTimezone = timezone.replace('Standard Time', '').trim();
        const currentTimezone = timezones.find(({ Id }) => {
          if (!Id) return false;
          const exactEquality = Id === timezone;
          const formattedEquality = Id.includes(formattedTimezone);

          return exactEquality || formattedEquality;
        });

        if (currentTimezone) {
          form.setFieldsValue({
            Timezone: currentTimezone.Id ?? '',
          });
        }
      }
    } catch (error) {
      showNotification(error);
    }
  }, [getTimezonesHandler, getBranchHandler, showNotification, form]);

  const initForm = useCallback(() => {
    if (!sendoutData) return;

    const {
      SendoutId,
      Name,
      Message,
      SendoutDelaySec,
      ReadAttachments,
      ReadTags,
      TagIds,
      Attachments,
      Timezone,
      MoS,
      MoE,
      TuS,
      TuE,
      WeS,
      WeE,
      ThS,
      ThE,
      FrS,
      FrE,
      SaS,
      SaE,
      SuS,
      SuE,
      IsNoSchedule,
    } = sendoutData;

    const files =
      (ReadAttachments ?? []).map((file) => ({
        uid: String(file.Id),
        name: file.FileName ?? 'Без имени',
        size: file.FileSize ?? undefined,
        type: file.Extension ?? undefined,
        status: 'done' as const,
        url: file.Thumbnail ?? undefined,
        response: file,
      })) ?? [];

    form.setFieldsValue({
      SendoutId,
      Name,
      Message,
      SendoutDelaySec,
      files,
      ReadTags: ReadTags || [],
      TagIds: TagIds || [],
      Attachments: Attachments || [],
      Timezone: Timezone || '',
      schedule: {
        monday: {
          active: MoS !== null && MoE !== null,

          startTime: MoS !== null ? dayjs(MoS, 'HH:mm:ss') : '',
          endTime: MoE !== null ? dayjs(MoE, 'HH:mm:ss') : '',
        },
        tuesday: {
          active: TuS !== null && TuS !== null,
          startTime: TuS !== null ? dayjs(TuS, 'HH:mm:ss') : '',
          endTime: TuE !== null ? dayjs(TuE, 'HH:mm:ss') : '',
        },
        wednesday: {
          active: WeS !== null && WeS !== null,
          startTime: WeS !== null ? dayjs(WeS, 'HH:mm:ss') : '',
          endTime: WeE !== null ? dayjs(WeE, 'HH:mm:ss') : '',
        },
        thursday: {
          active: ThS !== null && ThS !== null,
          startTime: ThS !== null ? dayjs(ThS, 'HH:mm:ss') : '',
          endTime: ThE !== null ? dayjs(ThE, 'HH:mm:ss') : '',
        },
        friday: {
          active: FrS !== null && FrS !== null,
          startTime: FrS !== null ? dayjs(FrS, 'HH:mm:ss') : '',
          endTime: FrE !== null ? dayjs(FrE, 'HH:mm:ss') : '',
        },
        saturday: {
          active: SaS !== null && SaS !== null,
          startTime: SaS !== null ? dayjs(SaS, 'HH:mm:ss') : '',
          endTime: SaE !== null ? dayjs(SaE, 'HH:mm:ss') : '',
        },
        sunday: {
          active: SuS !== null && SuS !== null,
          startTime: SuS !== null ? dayjs(SuS, 'HH:mm:ss') : '',
          endTime: SuE !== null ? dayjs(SuE, 'HH:mm:ss') : '',
        },
      },
      IsSchedule: !(IsNoSchedule ?? false),
    });
  }, [form, sendoutData]);

  const onFinish = useCallback(
    async ({ IsSchedule, schedule, files, ...values }: EditSendoutsForm) => {
      try {
        const attachments = [];

        for (const file of files) {
          const existing = sendoutData?.ReadAttachments?.find(
            (a) => a.FileName === file.name
          );

          let Data: string | undefined;

          if (file.originFileObj) {
            Data = await fileToBase64(file.originFileObj as File);
          }

          attachments.push({
            Id: existing ? existing.Id : 0,
            Name: file.name,
            Data: Data || '',
          });
        }

        await editSendout({
          ActiveBranchId,
          data: {
            ...sendoutData,
            ...values,
            Attachments: attachments,
            SendoutId: sendoutId,
            IsNoSchedule: !IsSchedule,
            MoS: IsSchedule ? getScheduleDate(schedule, 'monday') : null,
            MoE: IsSchedule
              ? getScheduleDate(schedule, 'monday', 'endTime')
              : null,
            TuS: IsSchedule ? getScheduleDate(schedule, 'tuesday') : null,
            TuE: IsSchedule
              ? getScheduleDate(schedule, 'tuesday', 'endTime')
              : null,
            WeS: IsSchedule ? getScheduleDate(schedule, 'wednesday') : null,
            WeE: IsSchedule
              ? getScheduleDate(schedule, 'wednesday', 'endTime')
              : null,
            ThS: IsSchedule ? getScheduleDate(schedule, 'thursday') : null,
            ThE: IsSchedule
              ? getScheduleDate(schedule, 'thursday', 'endTime')
              : null,
            FrS: IsSchedule ? getScheduleDate(schedule, 'friday') : null,
            FrE: IsSchedule
              ? getScheduleDate(schedule, 'friday', 'endTime')
              : null,
            SaS: IsSchedule ? getScheduleDate(schedule, 'saturday') : null,
            SaE: IsSchedule
              ? getScheduleDate(schedule, 'saturday', 'endTime')
              : null,
            SuS: IsSchedule ? getScheduleDate(schedule, 'sunday') : null,
            SuE: IsSchedule
              ? getScheduleDate(schedule, 'sunday', 'endTime')
              : null,
          },
        }).unwrap();

        showNotification('Рассылка успешно отредактирована', 'success');
        form.resetFields();
        onCancel();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      editSendout,
      sendoutId,
      showNotification,
      getScheduleDate,
      sendoutData,
      onCancel,
      form,
    ]
  );

  const isPageLoading = useMemo(
    () => isSendoutLoading || isSendoutFetching,
    [isSendoutFetching, isSendoutLoading]
  );

  const showSchedule = useMemo(() => !!formValues?.IsSchedule, [formValues]);

  const tagOptions: TagOptionType[] = useMemo(() => {
    const tags = sendoutData?.ReadTags || [];
    return tags.map(({ Id, Name, Color, IconId }) => ({
      value: Id,
      label: Name,
      color: Color ? getContrastYIQ(Color) : undefined,
      backgroundColor: Color ?? undefined,
      icon: IconId ?? undefined,
    }));
  }, [sendoutData]);

  return {
    form,
    onFinish,
    isPageLoading,
    initForm,
    showSchedule,
    initTimezones,
    timezones,
    tagOptions,
    onCancel,
  };
};
